/**
 * Pure reservation arithmetic (docs/specs/resource-reservation.md), shared by the API
 * (picker ranking, guidance payloads) and the UI (Guidance Outlook, matrix, load bars).
 *
 * A reservation reserves a Resource's UNGUIDED time — NOS capacity hours minus NOS guidance
 * hours — for one Account, through an optional inclusive end date. It never changes the
 * guidance hours themselves (those are NOS data) and never touches point-based capacity.
 */

/** The parts of a reservation the arithmetic needs. */
export interface ReservationWindow {
  accountId: string;
  /** Last day of the reservation, `YYYY-MM-DD`, inclusive. null = open-ended. */
  until: string | null;
}

/** Pure: `now` as a UTC calendar day, `YYYY-MM-DD` (the same UTC basis as workload-window). */
export function isoDay(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/** Pure: is the reservation still in force on `today` (`YYYY-MM-DD`)? The end date is inclusive. */
export function isReservationActive(window: ReservationWindow, today: string): boolean {
  return window.until === null || window.until >= today;
}

/**
 * Pure: does the reservation cover any part of `month`? Accepts `YYYY-MM` or `YYYY-MM-DD`; only
 * the month is compared, so a reservation ending mid-month still covers that month.
 */
export function reservationCoversMonth(
  window: Pick<ReservationWindow, "until">,
  month: string,
): boolean {
  return window.until === null || window.until.slice(0, 7) >= month.slice(0, 7);
}

/**
 * Pure: hours reserved for the Account in a month — capacity not already in NOS guidance.
 * null = unknown (no guidance or no capacity), mirroring `availabilityPercent`.
 * Overload reserves nothing (never negative).
 */
export function reservedHours(
  guidanceHours: number | null,
  capacityHours: number | null,
): number | null {
  if (guidanceHours === null) return null;
  if (capacityHours === null || capacityHours <= 0) return null;
  return Math.max(0, capacityHours - guidanceHours);
}

/** Ranking bucket for pickers: 0 = reserved for this Account, 1 = unreserved, 2 = reserved elsewhere. */
export type ReservationRank = 0 | 1 | 2;

/**
 * Pure: picker ordering bucket for a candidate on `accountId`'s work. Callers sort by this
 * first and keep their own ordering within a bucket, so reservation never hides anyone —
 * it only moves same-Account reservations to the top and other-Account reservations to the bottom.
 */
export function reservationRank(
  window: ReservationWindow | null,
  accountId: string | null,
  today: string,
): ReservationRank {
  if (window === null || !isReservationActive(window, today)) return 1;
  return window.accountId === accountId ? 0 : 2;
}
